/**
 * API client — humanizer planner (PR3 — 2026-05-10).
 *
 * Routes :
 *   - GET    /api/settings/publishing-pacing
 *   - PUT    /api/settings/publishing-pacing/{platform}
 *   - GET    /api/settings/publishing-windows[?platform=...]
 *   - PUT    /api/settings/publishing-windows
 *   - GET    /api/queue/calendar?from=...&to=...
 *   - PATCH  /api/queue/{id}/scheduled-for
 *   - GET    /api/queue/{id}/force-now-preview
 */

import { apiClient } from './client'
import type { components } from '@/types/api-generated'

export type PublishingPacingResponse = components['schemas']['PublishingPacingResponse']
export type PublishingPacingUpdateRequest = components['schemas']['PublishingPacingUpdateRequest']
export type PublishingPacingListResponse = components['schemas']['PublishingPacingListResponse']

export type PublishingWindowResponse = components['schemas']['PublishingWindowResponse']
export type PublishingWindowsUpdateRequest = components['schemas']['PublishingWindowsUpdateRequest']
export type PublishingWindowsListResponse = components['schemas']['PublishingWindowsListResponse']

export type HotHourBlock = components['schemas']['HotHourBlock']
export type ForceNowPreviewResponse = components['schemas']['ForceNowPreviewResponse']
export type QueueCalendarItem = components['schemas']['QueueCalendarItem']
export type QueueCalendarResponse = components['schemas']['QueueCalendarResponse']
export type DragDropScheduledForRequest = components['schemas']['DragDropScheduledForRequest']

type Platform = components['schemas']['Platform']

export const humanizerApi = {
  // ── Pacing ────────────────────────────────────────────────────────────
  listPacing: async (): Promise<PublishingPacingListResponse> => {
    const { data } = await apiClient.get<PublishingPacingListResponse>(
      '/settings/publishing-pacing',
    )
    return data
  },

  upsertPacing: async (
    platform: Platform,
    payload: PublishingPacingUpdateRequest,
  ): Promise<PublishingPacingResponse> => {
    const { data } = await apiClient.put<PublishingPacingResponse>(
      `/settings/publishing-pacing/${platform}`,
      payload,
    )
    return data
  },

  // ── Windows ───────────────────────────────────────────────────────────
  listWindows: async (platform?: Platform): Promise<PublishingWindowsListResponse> => {
    const { data } = await apiClient.get<PublishingWindowsListResponse>(
      '/settings/publishing-windows',
      platform ? { params: { platform } } : undefined,
    )
    return data
  },

  upsertWindows: async (
    payload: PublishingWindowsUpdateRequest,
  ): Promise<PublishingWindowsListResponse> => {
    const { data } = await apiClient.put<PublishingWindowsListResponse>(
      '/settings/publishing-windows',
      payload,
    )
    return data
  },

  // ── Queue calendar / drag-drop / force-now ────────────────────────────
  getCalendar: async (fromIso: string, toIso: string): Promise<QueueCalendarResponse> => {
    const { data } = await apiClient.get<QueueCalendarResponse>('/queue/calendar', {
      params: { from: fromIso, to: toIso },
    })
    return data
  },

  patchScheduledFor: async (
    queueItemId: number,
    payload: DragDropScheduledForRequest,
  ): Promise<components['schemas']['QueueItemResponse']> => {
    const { data } = await apiClient.patch<components['schemas']['QueueItemResponse']>(
      `/queue/${queueItemId}/scheduled-for`,
      payload,
    )
    return data
  },

  getForceNowPreview: async (queueItemId: number): Promise<ForceNowPreviewResponse> => {
    const { data } = await apiClient.get<ForceNowPreviewResponse>(
      `/queue/${queueItemId}/force-now-preview`,
    )
    return data
  },
}
